import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Trash2 } from "lucide-react";
import { adminApi, formatApiError } from "../../lib/adminApi";

export const AdminAccessCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const res = await adminApi.get("/admin/admins");
    setData(res.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (email) => {
    if (!window.confirm(`Remove admin access for ${email}? Their login stops working immediately.`)) return;
    setBusy(email);
    setError("");
    try {
      await adminApi.delete(`/admin/admins/${encodeURIComponent(email)}`);
      await load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail || "Remove failed"));
    }
    setBusy("");
  };

  if (!data) return null;

  return (
    <div className="mt-8 rounded-3xl border border-cream/10 p-6" data-testid="admin-access-card">
      <p className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">
        <ShieldCheck className="h-4 w-4 text-gold" />
        Admin access
      </p>
      <p className="mb-5 text-sm leading-relaxed text-cream/50">
        Accounts that can sign in to this dashboard. Removing one ends its access immediately.
      </p>
      <div className="space-y-3">
        {data.admins.map((a) => (
          <div
            key={a.email}
            className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-cream/10 px-4 py-3"
            data-testid={`admin-access-row-${a.email}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm text-cream/80">{a.email}</span>
              {a.is_you && (
                <span className="rounded-full border border-gold/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gold">
                  You
                </span>
              )}
              {a.is_env && (
                <span className="rounded-full border border-cream/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-cream/50">
                  Primary
                </span>
              )}
            </div>
            {!a.is_env && !a.is_you && (
              <button
                onClick={() => remove(a.email)}
                disabled={busy === a.email}
                data-testid={`admin-access-remove-${a.email}`}
                className="flex items-center gap-1.5 rounded-full border border-red-400/40 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-red-400 transition-colors duration-200 hover:bg-red-400/10 active:scale-95 disabled:opacity-50"
              >
                <Trash2 className="h-3 w-3" />
                {busy === a.email ? "Removing…" : "Remove"}
              </button>
            )}
          </div>
        ))}
      </div>
      {error && (
        <p className="mt-3 text-sm font-medium text-red-400" data-testid="admin-access-error">{error}</p>
      )}
    </div>
  );
};
